import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { toast } from 'react-toastify';

import { useAgencyLoginMutation, useAgencyVerifyOtpMutation } from '../slices/agencyAuthApiSlice';
import { setAgencyCredentials } from '../slices/authSlice';

// ─── OTP digit input component ────────────────────────────────────────────────
// Renders 6 individual boxes, auto-advances focus, handles paste.
// Mirrors the staff portal's OTPInput (AgencyRequests/frontend LoginScreen.jsx).
const OTPInput = ({ value, onChange, disabled }) => {
  const inputRefs = useRef([]);
  const digits = value.split('');

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      const next = [...digits];
      if (next[index]) {
        next[index] = '';
      } else if (index > 0) {
        next[index - 1] = '';
        inputRefs.current[index - 1]?.focus();
      }
      onChange(next.join(''));
    }
  };

  const handleChange = (index, e) => {
    const char = e.target.value.replace(/\D/g, '').slice(-1); // digits only
    const next = [...digits];
    next[index] = char;
    onChange(next.join(''));
    if (char && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    onChange(pasted.padEnd(6, '').slice(0, 6));
    const focusIdx = Math.min(pasted.length, 5);
    inputRefs.current[focusIdx]?.focus();
  };

  return (
    <Box sx={{ display: 'flex', gap: { xs: 0.75, sm: 1.5 }, justifyContent: 'center', my: 2 }}>
      {Array.from({ length: 6 }, (_, i) => (
        <Box
          key={i}
          component="input"
          ref={(el) => (inputRefs.current[i] = el)}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={digits[i] || ''}
          onChange={(e) => handleChange(i, e)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={i === 0 ? handlePaste : undefined}
          disabled={disabled}
          sx={{
            width: { xs: 40, sm: 52 },
            height: { xs: 48, sm: 60 },
            border: '2px solid',
            borderColor: digits[i] ? 'primary.main' : 'divider',
            borderRadius: 2,
            fontSize: { xs: '1.4rem', sm: '1.8rem' },
            fontWeight: 700,
            textAlign: 'center',
            fontFamily: 'monospace',
            bgcolor: digits[i] ? 'primary.50' : 'background.paper',
            color: 'primary.main',
            outline: 'none',
            transition: 'border-color 0.2s, background-color 0.2s',
            '&:focus': {
              borderColor: 'primary.dark',
              boxShadow: '0 0 0 3px rgba(25,118,210,0.15)',
            },
            '&:disabled': {
              bgcolor: '#f5f5f5',
              cursor: 'not-allowed',
            },
          }}
        />
      ))}
    </Box>
  );
};

const STEP_CREDENTIALS = 'credentials';
const STEP_OTP = 'otp';

const LoginScreen = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const [step, setStep] = useState(STEP_CREDENTIALS);
  const [otpValue, setOtpValue] = useState('');
  const [otpEmail, setOtpEmail] = useState(''); // confirmed email from step 1

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { agencyInfo } = useSelector((state) => state.agencyAuth);
  const [login, { isLoading, error }] = useAgencyLoginMutation();
  const [verifyOtp, { isLoading: verifying, error: otpError }] = useAgencyVerifyOtpMutation();

  const redirectTo = location.state?.from?.pathname || '/shifts/available';

  // ── Step 1: validate credentials, backend emails a 6-digit code ────────────
  const submitHandler = async (e) => {
    e.preventDefault();
    try {
      const res = await login({ email, password }).unwrap();
      if (res.otpSent) {
        setOtpEmail(res.email);
        setOtpValue('');
        setStep(STEP_OTP);
        toast.success(`We've emailed a login code to ${res.email}`);
      }
    } catch (err) {
      toast.error(err?.data?.error || err?.data?.message || 'Login failed');
    }
  };

  // ── Step 2: submit the OTP ──────────────────────────────────────────────────
  const handleOtpSubmit = async (e) => {
    e?.preventDefault?.();
    if (otpValue.length !== 6) return;
    try {
      const res = await verifyOtp({ email: otpEmail, otp: otpValue }).unwrap();
      dispatch(setAgencyCredentials(res));
      toast.success(`Welcome, ${res.name}`);
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setOtpValue('');
      toast.error(err?.data?.error || err?.data?.message || 'Invalid verification code');
    }
  };

  // Auto-submit once all 6 digits are entered
  // IMPORTANT: this hook (and every hook above it) must run on every render,
  // including the render right after login succeeds — so it has to sit
  // above the `if (agencyInfo) return ...` early return below. Putting an
  // early return before a hook is what caused "Rendered fewer hooks than
  // expected": the pre-login render reaches this useEffect and registers
  // it, but the very next render (once agencyInfo becomes truthy) used to
  // bail out before reaching it, so React saw one fewer hook than last time.
  useEffect(() => {
    if (step === STEP_OTP && otpValue.length === 6) {
      handleOtpSubmit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otpValue, step]);

  const backToCredentials = () => {
    setOtpValue('');
    setStep(STEP_CREDENTIALS);
  };

  // Already logged in — don't show the login form again. Must come after
  // every hook above so the hook count stays identical across renders.
  if (agencyInfo) {
    return <Navigate to={redirectTo} replace />;
  }

  return (
    <Box
      sx={{
        minHeight: '70vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Paper elevation={3} sx={{ p: 4, width: '100%', maxWidth: 400 }}>
        {step === STEP_CREDENTIALS && (
          <>
            <Typography variant="h5" sx={{ mb: 1, fontWeight: 700 }}>
              Agency Login
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Sign in with your agency account to view and supply available shifts.
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error?.data?.error || error?.data?.message || 'Login failed'}
              </Alert>
            )}

            <Box component="form" onSubmit={submitHandler} noValidate>
              <TextField
                label="Email"
                type="email"
                fullWidth
                required
                margin="normal"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoFocus
              />
              <TextField
                label="Password"
                type="password"
                fullWidth
                required
                margin="normal"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />

              <Button
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                sx={{ mt: 3 }}
                disabled={isLoading}
              >
                {isLoading ? <CircularProgress size={22} color="inherit" /> : 'Sign In'}
              </Button>
            </Box>
          </>
        )}

        {step === STEP_OTP && (
          <Box component="form" onSubmit={handleOtpSubmit}>
            <Typography variant="h5" sx={{ mb: 1, fontWeight: 700 }}>
              Verify Your Identity
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Enter the 6-digit code we emailed to
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, fontWeight: 600 }}>
              {otpEmail}
            </Typography>

            {otpError && (
              <Alert severity="error" sx={{ mb: 1 }}>
                {otpError?.data?.error || otpError?.data?.message || 'Invalid verification code'}
              </Alert>
            )}

            <OTPInput value={otpValue} onChange={setOtpValue} disabled={verifying} />

            <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              sx={{ mt: 2 }}
              disabled={verifying || otpValue.length !== 6}
            >
              {verifying ? <CircularProgress size={22} color="inherit" /> : 'Verify & Sign In'}
            </Button>

            <Button
              fullWidth
              size="small"
              sx={{ mt: 1.5 }}
              onClick={backToCredentials}
              disabled={verifying}
            >
              Back
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default LoginScreen;
